import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { ChronicleTtsVoicePicker } from './chronicles/ChronicleTtsVoicePicker';

// M5.14 (operator polish): admin view of recent chapters
// per team, with a per-chapter Regenerate button. The
// regenerate endpoint re-runs the Ollama generator with
// the LATEST theme + Ollama config, then overwrites the
// chapter row. Used when the operator edits the theme
// and wants existing chapters to reflect the new voice
// without waiting for the next hour's cron tick.
//
// "Stale" indicator: the chapter's theme_version hash
// is compared to the latest theme's hash. A stale chapter
// was written with an older theme; the regenerate button
// is highlighted so the operator knows at a glance which
// chapters would change on click.

interface Chapter {
  id: string;
  hour_utc: string;
  chapter_title: string;
  paragraph: string;
  theme_version: string;
  created_at: string;
  source: 'ai' | 'manual';
}

interface TeamChronicles {
  team_id: string;
  team_name: string;
  chapters: Chapter[];
  current_theme_version: string;
}

interface AdminChroniclesResponse {
  window_hours: number;
  generated_at: string;
  teams: TeamChronicles[];
}

interface RegenerateResponse {
  id: string;
  team_id: string;
  hour_utc: string;
  chapter_title: string;
  paragraph: string;
  theme_version: string;
  previous_theme_version: string;
  event_count: number;
}

interface Team {
  id: string;
  name: string;
  slug: string;
}

export function ChroniclesAdminPage() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useQuery<{ teams: Team[] }>({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });
  const teamIdFilter = searchParams.get('team_id') || '';
  const setTeamIdFilter = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team_id', id); else next.delete('team_id');
    setSearchParams(next, { replace: true });
  };

  const data = useQuery<AdminChroniclesResponse>({
    queryKey: ['admin-chronicles', teamIdFilter],
    // Pass team_id as both a query param + (when set)
    // a separate per-team endpoint. The /api/admin/teams
    // endpoint supports a single team OR an ?all=true
    // mode; we use the per-team endpoint here for
    // simplicity (the page is per-team by default).
    queryFn: () =>
      teamIdFilter
        ? api<AdminChroniclesResponse>(`/api/admin/teams/${teamIdFilter}/chronicles?hours=24`)
        : // No team filter -> show "pick a team" empty state.
          Promise.resolve({ window_hours: 24, generated_at: '', teams: [] }),
    enabled: !!teamIdFilter,
    refetchInterval: 60_000,
  });

  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const regenerate = useMutation<
    RegenerateResponse,
    Error,
    { chapterId: string },
    { previousData?: AdminChroniclesResponse }
  >({
    mutationFn: (vars) =>
      api<RegenerateResponse>(`/api/admin/chronicles/${vars.chapterId}/regenerate`, { method: 'POST' }),
    // Optimistic refresh: re-fetch the admin list AND
    // the public page data so the wall TV sees the new
    // chapter on the next 30s poll.
    onSuccess: (r) => {
      setFeedback({
        kind: 'ok',
        text: `Regenerated. ${r.event_count} events. New hash: ${r.theme_version.slice(0, 8)} · old: ${r.previous_theme_version.slice(0, 8)}`,
      });
      qc.invalidateQueries({ queryKey: ['admin-chronicles'] });
      qc.invalidateQueries({ queryKey: ['chronicles'] });
    },
    onError: (e) => {
      setFeedback({
        kind: 'err',
        text: e instanceof ApiError ? e.message : 'Regenerate failed.',
      });
    },
  });

  // M5.14 (operator polish): bulk regenerate. The
  // single-chapter button is fine for tuning one
  // chapter; the bulk action is the one-click 'apply
  // the new theme to everything in the last 24h' the
  // operator actually wants after editing the theme.
  // ~120s worst case (24 chapters × 5s) — the button
  // shows a pending state so the admin knows it's
  // working, not hung.
  const regenerateAll = useMutation<
    {
      regenerated: number;
      skipped: number;
      failed: number;
      results: Array<{ id: string; status: string; error?: string }>;
    },
    Error,
    void
  >({
    mutationFn: () =>
      api(`/api/admin/chronicles/regenerate-all?hours=24${teamIdFilter ? `&team_id=${encodeURIComponent(teamIdFilter)}` : ''}`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      const parts = [`${r.regenerated} regenerated`];
      if (r.skipped > 0) parts.push(`${r.skipped} skipped`);
      if (r.failed > 0) parts.push(`${r.failed} failed`);
      setFeedback({
        kind: r.failed > 0 ? 'err' : 'ok',
        text: `Bulk regenerate: ${parts.join(', ')}.`,
      });
      qc.invalidateQueries({ queryKey: ['admin-chronicles'] });
      qc.invalidateQueries({ queryKey: ['chronicles'] });
    },
    onError: (e) => {
      setFeedback({
        kind: 'err',
        text: e instanceof ApiError ? e.message : 'Bulk regenerate failed.',
      });
    },
  });

  // M5.14 (operator polish): 'Clean slate' variant —
  // same as the bulk regenerate above, but with
  // empty prior-chapter context per chapter. Use
  // this when a major theme change has left
  // contaminated prior chapters and the model keeps
  // re-injecting ghost references despite the
  // anti-leak system prompt. Each chapter is written
  // in isolation, so the chain self-heals on the next
  // cron run (which reads the freshly-written clean
  // chapters as prior context).
  const regenerateAllClean = useMutation<
    {
      regenerated: number;
      skipped: number;
      failed: number;
      results: Array<{ id: string; status: string; error?: string }>;
    },
    Error,
    void
  >({
    mutationFn: () =>
      api(`/api/admin/chronicles/regenerate-all?hours=24&clean=1${teamIdFilter ? `&team_id=${encodeURIComponent(teamIdFilter)}` : ''}`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      const parts = [`${r.regenerated} regenerated`];
      if (r.skipped > 0) parts.push(`${r.skipped} skipped`);
      if (r.failed > 0) parts.push(`${r.failed} failed`);
      setFeedback({
        kind: r.failed > 0 ? 'err' : 'ok',
        text: `Bulk regenerate: ${parts.join(', ')}.`,
      });
      qc.invalidateQueries({ queryKey: ['admin-chronicles'] });
      qc.invalidateQueries({ queryKey: ['chronicles'] });
    },
    onError: (e) => {
      setFeedback({
        kind: 'err',
        text: e instanceof ApiError ? e.message : 'Bulk regenerate failed.',
      });
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Admin</Link>
          <h1 className="mt-1 text-2xl font-semibold">Chronicles</h1>
          <p className="text-ink-muted">
            Recent chapters per team. Regenerate to re-run the generator with the
            latest theme + Ollama config (overwrites the row in place).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="chronicles-team" className="text-xs text-ink-muted">Team</label>
          <select
            id="chronicles-team"
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
            value={teamIdFilter}
            onChange={(e) => setTeamIdFilter(e.target.value)}
          >
            <option value="">Pick a team…</option>
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <button
            className="btn ml-2"
            onClick={() => regenerateAll.mutate()}
            disabled={regenerateAll.isPending || !teamIdFilter}
            title="Re-run the generator for EVERY chapter in the last 24h for this team. Sequential, ~1-5s per chapter. The single-chapter button is for tuning one row."
          >
            {regenerateAll.isPending ? 'Regenerating all…' : 'Regenerate all (24h)'}
          </button>
          <button
            className="btn ml-2"
            onClick={() => regenerateAllClean.mutate()}
            disabled={regenerateAllClean.isPending || !teamIdFilter}
            title="Re-run the generator for every chapter with EMPTY prior-chapter context. Use this when a major theme change leaves 'ghost' references in the regenerated prose (the model reads yesterday's prose and re-injects them). Each chapter is written in isolation; the next cron run resumes normal context reading."
          >
            {regenerateAllClean.isPending ? 'Cleaning all…' : 'Clean slate (24h)'}
          </button>
        </div>
      </header>

      {teamIdFilter ? <ChronicleTtsVoicePicker teamId={teamIdFilter} /> : null}

      {feedback ? (
        <div
          className={
            feedback.kind === 'ok'
              ? 'card border-l-4 border-l-accent-success text-sm'
              : 'card border-l-4 border-l-accent-danger text-sm'
          }
          role="status"
        >
          {feedback.text}
          <button className="btn ml-3" onClick={() => setFeedback(null)}>Dismiss</button>
        </div>
      ) : null}

      {!teamIdFilter ? (
        <p className="text-ink-muted">Pick a team above to see its recent chapters.</p>
      ) : data.isLoading ? (
        <p className="text-ink-muted">Loading…</p>
      ) : data.isError ? (
        <p className="text-accent-danger">
          Failed to load: {data.error instanceof ApiError ? data.error.message : String(data.error)}
        </p>
      ) : data.data && data.data.teams.length > 0 ? (
        <TeamView
          team={data.data.teams[0]!}
          isPending={regenerate.isPending}
          onRegenerate={(chapterId) => regenerate.mutate({ chapterId })}
        />
      ) : (
        <p className="text-ink-muted">No chapters in the last 24 hours.</p>
      )}
    </div>
  );
}

function TeamView({
  team,
  isPending,
  onRegenerate,
}: {
  team: TeamChronicles;
  isPending: boolean;
  onRegenerate: (chapterId: string) => void;
}) {
  if (team.chapters.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-ink-muted">
          No chapters in the last 24 hours for <strong>{team.team_name}</strong>.
          The cron writes one chapter per hour that has at least one event; a
          quiet day means no chapters.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
        {team.team_name} ({team.chapters.length} chapters)
      </h2>
      {team.chapters.map((c) => (
        <ChapterCard
          key={c.id}
          chapter={c}
          currentThemeVersion={team.current_theme_version}
          isPending={isPending}
          onRegenerate={() => onRegenerate(c.id)}
        />
      ))}
    </div>
  );
}

function ChapterCard({
  chapter,
  currentThemeVersion,
  isPending,
  onRegenerate,
}: {
  chapter: Chapter;
  currentThemeVersion: string;
  isPending: boolean;
  onRegenerate: () => void;
}) {
  // Stale indicator: chapter hash != current theme
  // hash. The button is highlighted so the operator
  // sees at a glance which chapters would change.
  const isStale = chapter.theme_version !== currentThemeVersion;
  return (
    <div className="card space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h3 className="text-base font-semibold">{chapter.chapter_title}</h3>
          {isStale ? (
            <span
              className="rounded-full bg-accent-warning/20 px-2 py-0.5 text-xs text-accent-warning"
              title={`This chapter was written with theme_version ${chapter.theme_version} but the current theme is ${currentThemeVersion}. Regenerate to apply the new theme.`}
            >
              stale theme
            </span>
          ) : (
            <span className="rounded-full bg-accent-success/20 px-2 py-0.5 text-xs text-accent-success">
              current theme
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-dim">
          <span className="font-mono">{chapter.theme_version.slice(0, 8)}</span>
          <span>·</span>
          <span>{new Date(chapter.hour_utc).toLocaleString()}</span>
        </div>
      </div>
      <p className="text-sm text-ink-muted line-clamp-3">{chapter.paragraph}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-dim">
          {chapter.source === 'manual' ? 'Manual chapter (regenerate disabled)' : 'AI-generated'}
        </span>
        {chapter.source === 'ai' ? (
          <button
            className={isStale ? 'btn-primary' : 'btn'}
            disabled={isPending}
            onClick={onRegenerate}
            title={isStale
              ? 'Re-run the chapter with the current theme. Old theme_version + new theme_version will be shown in the success toast.'
              : 'Re-run the chapter. Same theme, but the prose may differ (the generator is non-deterministic). Useful for a prose refresh without a theme change.'}
          >
            {isPending ? 'Regenerating…' : '↻ Regenerate'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
